/**
 * StepReady (Before You Start) — prep checklist with rating scale,
 * bias awareness, and three Culture Amp paste areas for multi-source feedback.
 */
import React, { useState } from 'react';
import { C, RATING_SCALE } from '../../../data/managerReviewData';
import type { Employee } from '../../../types/managerReview';
import { cleanCultureAmpPaste } from '../../../utils/cultureAmpParser';
import OnboardingShell from './OnboardingShell';

const CULTURE_AMP_URL = 'https://finn.eu.cultureamp.com/app/auth?redirect=https%3A%2F%2Ffinn.eu.cultureamp.com%2Fapp%2Fhome';

interface StepReadyProps {
  onNext: () => void;
  onBack: () => void;
  employee: Employee;
  selfReview: string;
  setSelfReview: (v: string) => void;
  perfFeedbackRaw: string;
  setPerfFeedbackRaw: (v: string) => void;
  anytimeFeedbackRaw: string;
  setAnytimeFeedbackRaw: (v: string) => void;
  cultureAmpMode: null | 'read' | 'paste';
  setCultureAmpMode: (v: null | 'read' | 'paste') => void;
}

function SectionCard({
  num, title, subtitle, statusBadge, children, openSections, onToggle,
}: {
  num: number; title: string; subtitle?: string; statusBadge?: React.ReactNode;
  children: React.ReactNode; openSections: Set<number>; onToggle: (n: number) => void;
}) {
  const open = openSections.has(num);
  return (
    <div style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
      <button
        onClick={() => onToggle(num)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px', background: open ? '#fafafa' : '#fff',
          border: 'none', cursor: 'pointer', fontFamily: 'Inter',
          borderBottom: open ? '1px solid #f0f0f0' : 'none', textAlign: 'left',
        }}
      >
        <div style={{
          width: 24, height: 24, borderRadius: 7, background: '#0f0f0f',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>{num}</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 800, fontSize: 13, color: '#0f0f0f', letterSpacing: '-0.01em' }}>{title}</span>
            {statusBadge}
          </div>
          {subtitle && <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>{subtitle}</div>}
        </div>
        <span style={{
          fontSize: 12, color: '#bbb',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s', display: 'inline-block',
        }}>▼</span>
      </button>
      {open && <div style={{ padding: '16px 18px' }}>{children}</div>}
    </div>
  );
}

/** Single paste area with auto-clean on paste */
function PasteArea({
  label, hint, value, onChange, placeholder, minHeight = 100,
}: {
  label: string; hint?: string; value: string; onChange: (v: string) => void;
  placeholder: string; minHeight?: number;
}) {
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const cleaned = cleanCultureAmpPaste(e.clipboardData.getData('text'));
    const el = e.currentTarget;
    const next = value.substring(0, el.selectionStart) + cleaned + value.substring(el.selectionEnd);
    onChange(next);
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.09em' }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: '#bbb', marginTop: 2, lineHeight: 1.45 }}>{hint}</div>}
      </div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        onPaste={handlePaste}
        placeholder={placeholder}
        style={{
          width: '100%', minHeight, borderRadius: 8, border: '1.5px solid #d1d5db',
          padding: '10px 12px', fontSize: 13, color: '#333', fontFamily: 'Inter',
          resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55, outline: 'none',
        }}
      />
      {value.trim() && (
        <div style={{ fontSize: 11, color: '#26bd59', marginTop: 4 }}>✓ Pasted</div>
      )}
    </div>
  );
}

export default function StepReady({
  onNext,
  onBack,
  employee,
  selfReview,
  setSelfReview,
  perfFeedbackRaw,
  setPerfFeedbackRaw,
  anytimeFeedbackRaw,
  setAnytimeFeedbackRaw,
  cultureAmpMode,
  setCultureAmpMode,
}: StepReadyProps) {
  const [openSections, setOpenSections] = useState(new Set([1]));
  const [confirmedSections, setConfirmedSections] = useState<Set<number>>(new Set());
  const first = employee.name.split(' ')[0];
  const canContinue = cultureAmpMode !== null;

  const confirmSection = (n: number, openNext?: number) => {
    setConfirmedSections(prev => new Set([...prev, n]));
    setOpenSections(prev => {
      const next = new Set(prev);
      next.delete(n);
      if (openNext) next.add(openNext);
      return next;
    });
  };

  const toggleSection = (n: number) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(n)) { next.delete(n); } else { next.add(n); }
      return next;
    });
  };

  const hasFeedbackContent = selfReview.trim() || perfFeedbackRaw.trim() || anytimeFeedbackRaw.trim();

  const cultureAmpBadge = cultureAmpMode === 'read'
    ? <span style={{ fontSize: 10, fontWeight: 700, color: '#26bd59', background: '#f0faf4', borderRadius: 4, padding: '2px 7px', letterSpacing: '0.05em' }}>✓ Done</span>
    : cultureAmpMode === 'paste' && hasFeedbackContent
      ? <span style={{ fontSize: 10, fontWeight: 700, color: '#26bd59', background: '#f0faf4', borderRadius: 4, padding: '2px 7px', letterSpacing: '0.05em' }}>✓ Pasted</span>
      : null;

  return (
    <OnboardingShell leftTitle="3 things to check before you start." wide>
      <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>

        {/* 1. Rating Scale */}
        <SectionCard
          num={1} title="Rating Scale" subtitle="Use this scale consistently when rating your direct report."
          statusBadge={confirmedSections.has(1) ? <span style={{ fontSize: 10, fontWeight: 700, color: '#26bd59', background: '#f0faf4', borderRadius: 4, padding: '2px 7px', letterSpacing: '0.05em' }}>✓ Done</span> : null}
          openSections={openSections} onToggle={toggleSection}
        >
          <div style={{ border: '1px solid #ebebeb', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '36px 155px 1fr 108px', background: '#f5f5f5', borderBottom: '1px solid #ebebeb', padding: '6px 12px', gap: 12 }}>
              {['#', 'Rating', 'Description', 'Eligibility'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.09em' }}>{h}</div>
              ))}
            </div>
            {RATING_SCALE.map((r, idx) => (
              <div key={r.v} style={{
                display: 'grid', gridTemplateColumns: '36px 155px 1fr 108px',
                padding: '10px 12px', gap: 12, alignItems: 'flex-start',
                borderBottom: idx < RATING_SCALE.length - 1 ? '1px solid #f0f0f0' : 'none',
                background: r.color + '0d',
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: 22, height: 22, borderRadius: 5, background: r.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#fff' }}>{r.v}</span>
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a', paddingTop: 2, lineHeight: 1.4 }}>{r.label}</div>
                <ul style={{ margin: 0, paddingLeft: 14, display: 'flex', flexDirection: 'column', gap: 2, listStyleType: 'disc' }}>
                  {r.desc.map((d, i) => <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.5 }}>{d}</li>)}
                </ul>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {(['meritIncrease', 'promotion'] as const).map(key => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{
                        width: 13, height: 13, borderRadius: 3,
                        border: `1.5px solid ${r[key] ? '#26bd59' : '#ccc'}`,
                        background: r[key] ? '#26bd59' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {r[key] && <span style={{ fontSize: 8, color: '#fff', fontWeight: 800 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 11, color: r[key] ? '#333' : '#aaa' }}>
                        {key === 'meritIncrease' ? 'Merit Increase' : 'Promotion'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button onClick={() => confirmSection(1, 2)} style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: C.blue, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter' }}>Got it →</button>
          </div>
        </SectionCard>

        {/* 2. Bias Check */}
        <SectionCard
          num={2} title="Bias" subtitle="Keep these in mind to ensure a fair and balanced assessment."
          statusBadge={confirmedSections.has(2) ? <span style={{ fontSize: 10, fontWeight: 700, color: '#26bd59', background: '#f0faf4', borderRadius: 4, padding: '2px 7px', letterSpacing: '0.05em' }}>✓ Done</span> : null}
          openSections={openSections} onToggle={toggleSection}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {[
              ['Recency bias', 'Overweighting the last few weeks instead of the full review period.'],
              ['Halo / Horn effect', 'One strong or weak area colours your view of everything else.'],
              ['Similarity bias', 'Rating people who think or work like you more favourably.'],
              ['Leniency / Strictness', 'Avoiding low ratings to stay comfortable, or being consistently harsh.'],
              ['Attribution bias', 'Crediting success to the person but blaming failure on circumstances — or vice versa.'],
            ].map(([name, desc]) => (
              <div key={name} style={{ display: 'flex', alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#333', flexShrink: 0, width: 158 }}>{name}</span>
                <span style={{ fontSize: 12, color: '#777', lineHeight: 1.55 }}>{desc}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button onClick={() => confirmSection(2, 3)} style={{ padding: '7px 16px', borderRadius: 7, border: 'none', background: C.blue, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter' }}>Got it →</button>
          </div>
        </SectionCard>

        {/* 3. Multi-Source Feedback */}
        <SectionCard
          num={3}
          title="Multi-Source Feedback"
          subtitle={`Review ${first}'s self-assessment and peer/upward feedback.`}
          statusBadge={cultureAmpBadge}
          openSections={openSections}
          onToggle={toggleSection}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', background: '#f8f8f8', border: '1px solid #e4e4e4', borderRadius: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
            <div style={{ fontSize: 12, color: '#888', lineHeight: 1.6 }}>
              <div>Only the delivery, behavior, and overall performance rating, plus your manager review text, are shared with your employee. Notes and sub-ratings are for your performance assessment only.</div>
              <div style={{ marginTop: 5 }}>You can make any edits and paste it directly into the Culture Amp Manager Review form, where it will be used for calibration and shared with the employee afterwards.</div>
            </div>
          </div>
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: '#aaa' }}>Choose how you'd like to proceed:</span>
            <a href={CULTURE_AMP_URL} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 12, color: C.blue, fontWeight: 600, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              Open Culture Amp ↗
            </a>
          </div>

          {cultureAmpMode === null && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setCultureAmpMode('read')} style={{ flex: 1, padding: '12px 14px', borderRadius: 8, border: '1.5px solid #e4e4e4', background: '#fff', cursor: 'pointer', fontFamily: 'Inter', textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f0f0f', marginBottom: 3 }}>✅ I've read through it</div>
                <div style={{ fontSize: 12, color: '#999', lineHeight: 1.5 }}>I'll keep the self-review and peer feedback in mind during my assessment.</div>
              </button>
              <button onClick={() => setCultureAmpMode('paste')} style={{ flex: 1, padding: '12px 14px', borderRadius: 8, border: '1.5px solid #e4e4e4', background: '#fff', cursor: 'pointer', fontFamily: 'Inter', textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f0f0f', marginBottom: 3 }}>📋 Paste it here for reference</div>
                <div style={{ fontSize: 12, color: '#999', lineHeight: 1.5 }}>Copy in the reviews to give full context during the assessment.</div>
              </button>
            </div>
          )}

          {cultureAmpMode === 'read' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: '#f0faf4', border: '1px solid #b6e8c8', borderRadius: 8 }}>
              <span style={{ fontSize: 16 }}>✅</span>
              <div style={{ flex: 1, fontSize: 13, color: '#1a6b3c', fontWeight: 600 }}>Got it — you'll keep the feedback in mind.</div>
              <button onClick={() => setCultureAmpMode(null)} style={{ fontSize: 11, color: '#999', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter' }}>Change</button>
            </div>
          )}

          {cultureAmpMode === 'paste' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Instruction */}
              <div style={{ fontSize: 12, color: '#888', background: '#f8f8f8', borderRadius: 7, padding: '8px 12px', lineHeight: 1.55, marginBottom: 10 }}>
                💡 Open each review in Culture Amp, select all and copy (<strong>Cmd+A</strong> → <strong>Cmd+C</strong>), then paste it in the relevant box. Don't worry about formatting — anything you paste will be automatically cleaned up and shown in a structured, readable format alongside your assessment.
              </div>

              {/* Self-review */}
              <PasteArea
                label="Self-Review"
                hint="The employee's own self-assessment from the Performance Review Cycle."
                value={selfReview}
                onChange={setSelfReview}
                placeholder={`Paste ${first}'s self-review from Culture Amp here…`}
                minHeight={160}
              />

              {/* Peer & upward feedback */}
              <PasteArea
                label="Peer & Upward Feedback"
                hint="Peer and/or upward reviews from the Performance Review Cycle."
                value={perfFeedbackRaw}
                onChange={setPerfFeedbackRaw}
                placeholder="Paste all peer and upward feedback here — you can paste multiple reviewers at once."
                minHeight={160}
              />

              {/* Anytime feedback */}
              <PasteArea
                label="Anytime Feedback"
                hint="Ongoing feedback collected outside the Performance Review Cycle (peers, stakeholders, direct reports)."
                value={anytimeFeedbackRaw}
                onChange={setAnytimeFeedbackRaw}
                placeholder="Paste anytime feedback here — you can paste multiple reviewers at once."
                minHeight={130}
              />

              <div style={{ marginTop: 4 }}>
                <button onClick={() => setCultureAmpMode(null)} style={{ fontSize: 11, color: '#bbb', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Inter', padding: 0 }}>← Change option</button>
              </div>
            </div>
          )}
        </SectionCard>

        <div style={{ height: 8 }} />
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 20, flexShrink: 0 }}>
        <button
          onClick={onBack}
          style={{
            fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
            border: '1.5px solid #e5e7eb', cursor: 'pointer', borderRadius: 8,
            padding: '12px 20px', background: 'transparent', color: '#1a1a1a',
          }}
        >← Back</button>
        <button
          onClick={onNext}
          disabled={!canContinue}
          style={{
            fontFamily: 'Inter', fontWeight: 700, fontSize: 15,
            border: 'none', cursor: canContinue ? 'pointer' : 'not-allowed',
            borderRadius: 8, padding: '14px 20px',
            background: C.blue, color: '#fff', flex: 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            opacity: canContinue ? 1 : 0.45,
          }}
        >
          {canContinue ? 'Begin Review →' : 'Set up feedback to continue'}
        </button>
      </div>
    </OnboardingShell>
  );
}
